import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { Users, FolderPlus, Shield, Plus, X, UserPlus } from 'lucide-react';

interface AdminPanelProps {
  db: any;
  uid: string;
}

interface User {
  id: string;
  email: string;
  bulk_treatments_rights: boolean;
  ai_chat_rights: boolean;
  created_at: any;
}

interface Project {
  id: string;
  name: string;
  created_at: any;
  user_ids: string[];
}

type TabType = 'projects' | 'users' | 'permissions';

const AdminPanel: React.FC<AdminPanelProps> = ({ db, uid }) => {
  const [activeTab, setActiveTab] = useState<TabType>('projects');
  const [users, setUsers] = useState<User[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form states
  const [newProjectName, setNewProjectName] = useState('');
  const [selectedProjectForUsers, setSelectedProjectForUsers] = useState<string>('');
  const [userEmailToAdd, setUserEmailToAdd] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const usersRef = collection(db, 'users');
      const q = query(usersRef, orderBy('created_at', 'desc'));
      const snapshot = await getDocs(q);
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as User[];
      setUsers(usersData);
    } catch (err) {
      setError('Failed to load users: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [db]);

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const projectsRef = collection(db, 'projects');
      const q = query(projectsRef, orderBy('created_at', 'desc'));
      const snapshot = await getDocs(q);
      const projectsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Project[];
      setProjects(projectsData);
    } catch (err) {
      setError('Failed to load projects: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProjectName.trim()) return;

    try {
      setLoading(true);
      setError(null);
      
      const projectsRef = collection(db, 'projects');
      const newProject = {
        name: newProjectName.trim(),
        created_at: serverTimestamp(),
        user_ids: [uid] // Creator is automatically added
      };
      
      await addDoc(projectsRef, newProject);
      setNewProjectName('');
      await loadProjects();
    } catch (err) {
      setError('Failed to create project: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBulkRights = async (userId: string, currentStatus: boolean) => {
    try {
      setLoading(true);
      setError(null);
      
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        bulk_treatments_rights: !currentStatus
      });
      
      await loadUsers();
    } catch (err) {
      setError('Failed to update user permissions: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAiChatRights = async (userId: string, currentStatus: boolean) => {
    try {
      setLoading(true);
      setError(null);

      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        ai_chat_rights: !currentStatus
      });

      await loadUsers();
    } catch (err) {
      setError('Failed to update user permissions: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddUserToProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProjectForUsers || !userEmailToAdd.trim()) return;

    try {
      setLoading(true);
      setError(null);

      // Find user by email
      const user = users.find(u => u.email.toLowerCase() === userEmailToAdd.toLowerCase());
      if (!user) {
        setError('User not found with that email address');
        return;
      }

      // Find project
      const project = projects.find(p => p.id === selectedProjectForUsers);
      if (!project) {
        setError('Project not found');
        return;
      }

      // Check if user already has access
      if (project.user_ids.includes(user.id)) {
        setError('User already has access to this project');
        return;
      }

      // Update project with new user
      const projectRef = doc(db, 'projects', selectedProjectForUsers);
      await updateDoc(projectRef, {
        user_ids: [...project.user_ids, user.id]
      });

      setUserEmailToAdd('');
      await loadProjects();
    } catch (err) {
      setError('Failed to add user to project: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUserFromProject = async (projectId: string, userId: string) => {
    try {
      setLoading(true);
      setError(null);

      const project = projects.find(p => p.id === projectId);
      if (!project) return;

      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, {
        user_ids: project.user_ids.filter(id => id !== userId)
      });

      await loadProjects();
    } catch (err) {
      setError('Failed to remove user from project: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const getUserEmail = (userId: string): string => {
    const user = users.find(u => u.id === userId);
    return user?.email || 'Unknown user';
  };

  return (
    <div className="text-gray-100 flex flex-col">
      
      <main className="flex-grow w-full max-w-7xl mx-auto p-4 md:p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Admin Panel</h1>
          <p className="text-gray-400">Manage projects, users, and permissions</p>
        </div>

        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start gap-3">
            <div className="text-red-400">⚠️</div>
            <div>
              <p className="text-red-300 font-medium">Error</p>
              <p className="text-red-400 text-sm">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-700">
          <button
            onClick={() => setActiveTab('projects')}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
              activeTab === 'projects'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <FolderPlus className="w-5 h-5" />
            Projects
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
              activeTab === 'users'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <UserPlus className="w-5 h-5" />
            Add Users to Projects
          </button>
          <button
            onClick={() => setActiveTab('permissions')}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
              activeTab === 'permissions'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <Shield className="w-5 h-5" />
            Permissions
          </button>
        </div>

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="space-y-6">
            {/* Create Project Form */}
            <div className="bg-gray-800/60 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5 text-blue-400" />
                Create New Project
              </h2>
              <form onSubmit={handleCreateProject} className="flex gap-3">
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="Enter project name..."
                  className="flex-1 bg-gray-900/50 border border-gray-700 text-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !newProjectName.trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Create
                </button>
              </form>
            </div>

            {/* Projects List */}
            <div className="bg-gray-800/60 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <FolderPlus className="w-5 h-5 text-green-400" />
                All Projects ({projects.length})
              </h2>
              
              {loading && projects.length === 0 ? (
                <p className="text-gray-400 text-center py-8">Loading projects...</p>
              ) : projects.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No projects yet. Create your first one!</p>
              ) : (
                <div className="space-y-3">
                  {projects.map((project) => (
                    <div key={project.id} className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-white">{project.name}</h3>
                        <p className="text-sm text-gray-400 mt-1">
                          {project.user_ids.length} user{project.user_ids.length !== 1 ? 's' : ''} • Created {project.created_at?.toDate?.()?.toLocaleDateString?.() || 'Recently'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Add User to Project Form */}
            <div className="bg-gray-800/60 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-blue-400" />
                Add User to Project
              </h2>
              <form onSubmit={handleAddUserToProject} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Select Project</label>
                  <select
                    value={selectedProjectForUsers}
                    onChange={(e) => setSelectedProjectForUsers(e.target.value)}
                    className="w-full bg-gray-900/50 border border-gray-700 text-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    disabled={loading}
                  >
                    <option value="">Choose a project...</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">User Email</label>
                  <input
                    type="email"
                    value={userEmailToAdd}
                    onChange={(e) => setUserEmailToAdd(e.target.value)}
                    placeholder="user@example.com"
                    className="w-full bg-gray-900/50 border border-gray-700 text-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    disabled={loading}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !selectedProjectForUsers || !userEmailToAdd.trim()}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <UserPlus className="w-5 h-5" />
                  Add User to Project
                </button>
              </form>
            </div>

            {/* Projects with Users */}
            <div className="bg-gray-800/60 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-green-400" />
                Project Access Management
              </h2>
              
              {projects.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No projects available</p>
              ) : (
                <div className="space-y-4">
                  {projects.map((project) => (
                    <div key={project.id} className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                      <h3 className="font-semibold text-white mb-3">{project.name}</h3>
                      {project.user_ids.length === 0 ? (
                        <p className="text-gray-500 text-sm">No users have access yet</p>
                      ) : (
                        <div className="space-y-2">
                          {project.user_ids.map((userId) => (
                            <div key={userId} className="flex items-center justify-between bg-gray-800/50 rounded-lg p-3">
                              <span className="text-gray-300 text-sm">{getUserEmail(userId)}</span>
                              <button
                                onClick={() => handleRemoveUserFromProject(project.id, userId)}
                                disabled={loading}
                                className="text-red-400 hover:text-red-300 p-1 rounded hover:bg-red-500/10 transition-colors disabled:opacity-50"
                                title="Remove access"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Permissions Tab */}
        {activeTab === 'permissions' && (
          <div className="bg-gray-800/60 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-400" />
              Permissions
            </h2>
            <p className="text-gray-400 mb-6">Manage user permissions</p>

            {loading && users.length === 0 ? (
              <p className="text-gray-400 text-center py-8">Loading users...</p>
            ) : users.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No users found</p>
            ) : (
              <div className="space-y-3">
                {users.map((user) => (
                  <div key={user.id} className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-white">{user.email}</p>
                      <p className="text-sm text-gray-400 mt-1">
                        User ID: {user.id}
                      </p>
                    </div>
                    <div className="flex items-center gap-6">
                      {/* Bulk Treatments toggle */}
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-xs text-gray-400">Bulk Treatments</span>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${user.bulk_treatments_rights ? 'text-green-400' : 'text-gray-500'}`}>
                            {user.bulk_treatments_rights ? 'On' : 'Off'}
                          </span>
                          <button
                            onClick={() => handleToggleBulkRights(user.id, user.bulk_treatments_rights)}
                            disabled={loading}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              user.bulk_treatments_rights ? 'bg-blue-600' : 'bg-gray-600'
                            } disabled:opacity-50`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                user.bulk_treatments_rights ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                      {/* AI Chat toggle */}
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-xs text-gray-400">AI Chat</span>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${user.ai_chat_rights ? 'text-green-400' : 'text-gray-500'}`}>
                            {user.ai_chat_rights ? 'On' : 'Off'}
                          </span>
                          <button
                            onClick={() => handleToggleAiChatRights(user.id, user.ai_chat_rights)}
                            disabled={loading}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              user.ai_chat_rights ? 'bg-purple-600' : 'bg-gray-600'
                            } disabled:opacity-50`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                user.ai_chat_rights ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPanel;
